// ============================================
// ConverseTech Frontend - Settings Page
// ============================================

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Shield, Trash2 } from 'lucide-react';
import { getCurrentUser, updateUser } from '../lib/api';

export default function Settings() {
  const queryClient = useQueryClient();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [saved, setSaved] = useState(false);

  const { data: user, isLoading } = useQuery({
    queryKey: ['user'],
    queryFn: getCurrentUser,
  });

  const updateMutation = useMutation({
    mutationFn: updateUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  const handleRecordingToggle = () => {
    if (user) {
      updateMutation.mutate({ recordingEnabled: !user.recordingEnabled });
    }
  };

  const handleRevokeConsent = () => {
    if (user) {
      updateMutation.mutate({ hasConsented: false });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <Link to="/dashboard" className="flex items-center gap-2 text-gray-500 hover:text-gray-700">
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-2xl">
        <h1 className="text-2xl font-bold text-gray-900 mb-8">Settings</h1>

        {/* Privacy Settings */}
        <section data-testid="privacy-settings" className="card p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="w-5 h-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">Privacy Settings</h2>
          </div>

          {/* Recording Toggle */}
          <div className="flex items-center justify-between py-4 border-b border-gray-100">
            <div>
              <h3 className="font-medium text-gray-900">Session Recording</h3>
              <p data-testid="recording-description" className="text-sm text-gray-500">
                Save audio recordings for later review
              </p>
            </div>
            <button
              data-testid="recording-toggle"
              data-checked={user?.recordingEnabled ? 'true' : 'false'}
              onClick={handleRecordingToggle}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                user?.recordingEnabled ? 'bg-primary-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  user?.recordingEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Consent Status */}
          {user?.hasConsented && (
            <div className="flex items-center justify-between py-4 border-b border-gray-100">
              <div>
                <h3 className="font-medium text-gray-900">Privacy Consent</h3>
                <p className="text-sm text-gray-500">You have consented to voice processing</p>
              </div>
              <button
                data-testid="revoke-consent-button"
                onClick={handleRevokeConsent}
                className="text-sm text-red-600 hover:text-red-700"
              >
                Revoke Consent
              </button>
            </div>
          )}

          {/* Delete Data */}
          <div className="flex items-center justify-between py-4">
            <div>
              <h3 className="font-medium text-gray-900">Delete All Data</h3>
              <p className="text-sm text-gray-500">Permanently delete all your session data</p>
            </div>
            <button
              data-testid="delete-data-button"
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 border border-red-200 rounded hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4" />
              Delete Data
            </button>
          </div>
        </section>

        {/* Saved Indicator */}
        {saved && (
          <div data-testid="settings-saved" className="text-center text-green-600 text-sm">
            Settings saved successfully
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div data-testid="delete-confirmation-dialog" className="bg-white rounded-xl p-6 max-w-sm mx-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Delete All Data?</h2>
              <p className="text-gray-500 mb-4">
                This action cannot be undone. All your sessions, transcripts, and recordings will be
                permanently deleted.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 py-2 bg-gray-100 hover:bg-gray-200 rounded text-gray-700"
                >
                  Cancel
                </button>
                <button className="flex-1 py-2 bg-red-600 hover:bg-red-700 rounded text-white">
                  Delete Everything
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
